/**
 * Centralized candidate naming system
 *
 * Format: {shapeId}.{optimizationCount}
 *
 * Examples:
 * - "log-accel-15.0" - Base candidate, no optimizations
 * - "log-accel-15.1" - One optimization applied
 * - "log-accel-15.2" - Two optimizations applied
 *
 * The optimization count corresponds to the number of entries in optimizationHistory
 * that are actual optimizations (excluding 'initial-shape' and 'optimization-skipped').
 *
 * Display: UI should show just the shapeId (e.g., "log-accel-15")
 * The ".N" suffix indicates how many optimizations to expect in the history.
 */

import { logger } from '~/services/logging';

export interface CandidateNameComponents {
  /** The base shape ID (e.g., "log-accel-15") */
  shapeId: string;
  /** Number of optimizations applied (0 = base candidate) */
  optimizationCount: number;
}

export class CandidateNaming {
  /**
   * Parse a candidate name into its components
   *
   * Handles both new format ({shapeId}.{count}) and legacy formats
   */
  static parse(name: string): CandidateNameComponents {
    logger.L3.debug(`Parsing candidate name`, { name });

    // New format: {shapeId}.{count} (e.g., "log-accel-15.2")
    // Match the last .{number} at the end of the string
    const newFormatMatch = name.match(/^(.+)\.(\d+)$/);
    if (newFormatMatch) {
      const [, shapeId, countStr] = newFormatMatch;
      const optimizationCount = parseInt(countStr, 10);
      logger.L3.debug(`Parsed new format`, { shapeId, optimizationCount });
      return { shapeId, optimizationCount };
    }

    // Legacy format: {shape} | {process} (e.g., "log-accel-15 | OPT-01")
    if (name.includes('|')) {
      const parts = name.split('|').map((p) => p.trim());
      const shapeId = parts[0];
      const processStr = parts[1] || '';

      // Extract iteration number from process string
      const optMatch = processStr.match(/OPT-(\d+)/);
      const optimizationCount = optMatch ? parseInt(optMatch[1], 10) : 0;

      logger.L3.debug(`Parsed legacy pipe format`, { shapeId, optimizationCount });
      return { shapeId, optimizationCount };
    }

    // Legacy format with suffixes: {shapeId}-OPT-{nn} or {shapeId}_custom-{n}
    const legacyOptMatch = name.match(/^(.+)-OPT-(\d+)$/);
    if (legacyOptMatch) {
      const [, shapeId, countStr] = legacyOptMatch;
      const optimizationCount = parseInt(countStr, 10);
      logger.L3.debug(`Parsed legacy OPT format`, { shapeId, optimizationCount });
      return { shapeId, optimizationCount };
    }

    const customMatch = name.match(/^(.+)_custom-(\d+)$/);
    if (customMatch) {
      const [, shapeId, countStr] = customMatch;
      const optimizationCount = parseInt(countStr, 10);
      logger.L3.debug(`Parsed legacy custom format`, { shapeId, optimizationCount });
      return { shapeId, optimizationCount };
    }

    // Default: assume it's just a shape with no optimizations
    logger.L3.debug(`No format matched, treating as base shape`, { shapeId: name });
    return { shapeId: name, optimizationCount: 0 };
  }

  /**
   * Build a candidate name from components
   *
   * @param shapeId - The base shape ID
   * @param optimizationCount - Number of optimizations applied (0 = base)
   */
  static build(shapeId: string, optimizationCount: number): string {
    const result = `${shapeId}.${optimizationCount}`;
    logger.L3.debug(`Built candidate name`, { shapeId, optimizationCount, result });
    return result;
  }

  /**
   * Extract just the shape ID from any candidate name
   *
   * This is what should be displayed in the UI
   */
  static getShapeId(name: string): string {
    const components = CandidateNaming.parse(name);
    return components.shapeId;
  }

  /**
   * Get the optimization count from a candidate name
   */
  static getOptimizationCount(name: string): number {
    const components = CandidateNaming.parse(name);
    return components.optimizationCount;
  }

  /**
   * Create a name for an optimization iteration
   *
   * @param baseName - The current candidate name
   * @param newOptimizationCount - The new total optimization count
   */
  static createOptimizationName(baseName: string, newOptimizationCount: number): string {
    logger.L3.info(`Creating optimization name`, {
      input: baseName,
      newOptCount: newOptimizationCount,
    });

    // Extract just the shape ID (strip any existing suffix)
    const shapeId = CandidateNaming.getShapeId(baseName);

    const result = CandidateNaming.build(shapeId, newOptimizationCount);

    logger.L3.info(`Created optimization name`, {
      from: baseName,
      to: result,
    });

    return result;
  }

  /**
   * Create a name for a base candidate (no optimizations)
   */
  static createBaseName(shapeId: string): string {
    return CandidateNaming.build(shapeId, 0);
  }
}
